import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import { Lesson } from '../models/lesson';
import { LINK_ACTION_TYPES, LinkActionType } from '../models/link-action';
import { Post } from '../models/post';
import { GeneralService } from './general.service';

export interface OpenLessonLinkOptions {
  /** Produto dono da aula; sem ele cai em lesson.product_id. */
  productId?: number | string;
  /** URL a abrir; sem ela cai em external_url. */
  url?: string;
  /** Tipo da action clicada (ex.: botão do destaque); vence o tipo da própria aula. */
  type?: string;
  extras?: NavigationExtras;
}

export interface OpenLinkOptions {
  /** Flag legada que pedia abertura dentro do app; só vale quando o tipo chega como EXTERNAL_URL. */
  legacyInApp?: boolean;
  /** Título exibido no cabeçalho do visualizador. */
  title?: string;
  replaceUrl?: boolean;
}

export interface StoredLinkFrame {
  url: string;
  title?: string;
  backUrl?: string;
}

const SAFE_URL: RegExp = /^https?:\/\//i;

/** Só http(s): javascript: e data: num iframe rodariam na origem do PWA. */
export function isSafeLinkUrl(url: string | null | undefined): boolean {
  return !!url && SAFE_URL.test(url.trim());
}

interface StoredInternalUrl {
  id: number;
  url: string;
}

/**
 * Decide onde abre um link (ED-3006): WEBVIEW_URL no iframe do app, INTERNAL_BROWSER_URL
 * e EXTERNAL_URL em nova aba. Enquanto a web recebe EXTERNAL_URL rebaixado, as flags
 * legadas open_in_browser / open_external continuam valendo.
 */
@Injectable({ providedIn: 'root' })
export class ExternalLinkService {

  private static readonly INTERNAL_URL_KEY: string = 'external_url_internal';
  private static readonly LINK_FRAME_STATE: string = 'linkFrame';

  constructor(
    private generalService: GeneralService,
    private router: Router
  ) {}

  isSafeUrl(url: string | null | undefined): boolean {
    return isSafeLinkUrl(url);
  }

  isLinkType(type: string | null | undefined): type is LinkActionType {
    return LINK_ACTION_TYPES.includes(type as LinkActionType);
  }

  opensInApp(type: string | null | undefined, legacyInApp: boolean = false): boolean {
    if (type === 'WEBVIEW_URL') return true;
    if (type === 'INTERNAL_BROWSER_URL') return false;
    return legacyInApp;
  }

  /** Retorna true quando abriu dentro do app (iframe). */
  openLink(url: string, type: string | null | undefined, options: OpenLinkOptions = {}): boolean {
    if (!this.isSafeUrl(url)) return false;
    if (!this.opensInApp(type, options.legacyInApp)) {
      this.openInBrowser(url);
      return false;
    }
    this.openInFrame(url, options);
    return true;
  }

  openInFrame(url: string, options: OpenLinkOptions = {}): void {
    // Com replaceUrl a tela atual sai do histórico, então ela não serve de volta.
    const payload: StoredLinkFrame = {
      url: url,
      title: options.title,
      backUrl: options.replaceUrl ? undefined : this.router.url,
    };
    // No state da navegação cada entrada do histórico guarda o próprio link, sem o limite de tamanho do cookie.
    this.router.navigate(['navegador'], {
      replaceUrl: options.replaceUrl === true,
      state: { [ExternalLinkService.LINK_FRAME_STATE]: payload },
    });
  }

  readLinkFrame(): StoredLinkFrame | null {
    const state: Record<string, unknown> | null = window.history.state;
    const stored: StoredLinkFrame | undefined = state?.[ExternalLinkService.LINK_FRAME_STATE] as StoredLinkFrame | undefined;
    return stored && this.isSafeUrl(stored.url) ? stored : null;
  }

  /** CTA do post: cta_action traz o modo; sem ele vale a URL crua de sempre. */
  openPostCta(post: Post): boolean {
    const url: string = post?.cta_action?.url || post?.call_to_action_link || '';
    return this.openLink(url, post?.cta_action?.type);
  }

  lessonUrl(lesson: Lesson): string {
    return lesson?.external_url || '';
  }

  /** Retorna true quando abriu internamente (navegou para o viewer em iframe). */
  openLessonLink(lesson: Lesson, options: OpenLessonLinkOptions = {}): boolean {
    const url: string = options.url || this.lessonUrl(lesson);
    const productId: number | string = options.productId ?? lesson?.product_id;

    if (!this.isSafeUrl(url)) {
      if (productId) this.router.navigate(['produtos', productId], options.extras || {});
      return false;
    }

    const inApp: boolean = this.opensInApp(options.type ?? lesson?.action?.type, !lesson?.open_in_browser);
    // Sem produto/aula nao existe rota de viewer; nova aba ainda e melhor que barrar.
    if (!inApp || !productId || !lesson?.id) {
      this.openInBrowser(url);
      return false;
    }

    this.storeInternalUrl(lesson.id, url);
    this.router.navigate(
      ['produtos', productId, 'aula', lesson.id, 'viewer'],
      options.extras || {}
    );
    return true;
  }

  openInBrowser(url: string): void {
    const anchor: HTMLAnchorElement = document.createElement('a');
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.href = url;
    anchor.dispatchEvent(new MouseEvent('click'));
  }

  storeInternalUrl(lessonId: number, url: string): void {
    const payload: StoredInternalUrl = { id: lessonId, url: url };
    this.generalService.store(payload, ExternalLinkService.INTERNAL_URL_KEY, true);
  }

  /** Só devolve a URL guardada se ela pertencer a esta aula. */
  readInternalUrl(lessonId: number): string {
    const raw: unknown = this.generalService.get(ExternalLinkService.INTERNAL_URL_KEY, false);
    if (typeof raw !== 'string' || !raw) return '';
    try {
      const stored: StoredInternalUrl = JSON.parse(raw);
      return stored?.id === lessonId ? stored.url || '' : '';
    } catch {
      return '';
    }
  }
}
